/**
 * How long each part of a song lasts, in bars and in seconds.
 *
 * This is the musical half of Smart Auto Scroll — pure functions over the song data, no DOM. The
 * pixel half lives in smartScrollSchedule.js, which pairs these durations with the measured
 * heights of the rendered parts.
 *
 * Durations come from three sources, best first:
 *   1. the chart stored on a part whose body is words — bars, where they are the part's own (see
 *      chartBars)
 *   2. a {Nx,seconds} directive — a pass of the part, timed with a stopwatch
 *   3. bars x seconds-per-bar, with bars read from a chart's bar lines where it has them, and
 *      otherwise counted off the lyrics or the chords
 *
 * A chart outranks the stopwatch because it is current notation where a directive is an older
 * reading, and because it says how many bars the part is rather than how many seconds it took once.
 * A part carrying both is still read for the directive's pass count — see getRepeatCount.
 *
 * The line between real data and an estimate does not fall between those, it falls inside the
 * last. A part written out in bar lines is the chart stating its own length, and bars x
 * seconds-per-bar is then arithmetic rather than a guess; a part counted off its lyrics lines is an
 * estimate calibrated against a recording. `measured` on the returned duration is that distinction,
 * and the scroll engine reads it: the player's pace multiplier corrects the parts that were
 * estimated and leaves the ones that were measured at the length the chart gave them.
 *
 * part.midi.duration used to sit between them, and deliberately does not any more. A clip's length
 * only tells you how long the part runs if the clip is exactly one pass of it, and nothing in the
 * stored MIDI says whether it is: someone may record a single repeat because the melody repeats,
 * or the whole part including its repeats, or just the hook as a reminder of the tune. Reading it
 * as one pass (which is what multiplying by repeatCount did) turns a full recording of a part
 * played twice into double its real length. An unmarked guess that can be wrong by a factor of
 * repeatCount is worse than the honest estimate below, which is at least calibrated.
 */

import {
  BARS_PER_LYRIC_LINE,
  DEFAULT_INSTRUMENTAL_BARS,
  MIN_BARS_PER_CHORD_LINE,
  MIN_INSTRUMENTAL_BARS,
  NOTE_BARS,
  snapToPhrase,
} from './autoScrollConstants';
import { chartDataToText, hasChartData } from './chartData';
import { chartBars, isChartDrawn } from './partCharts';
import { countBars, findChords } from './chordPatterns';
import { countManualRepeats, isChordLine, isLyricsLine, parseRepeatDirective } from './repeat';
import { isSectionPart } from './partNumbers';
import { getPartBpm } from './partTempo';

/** A Note is guidance for the player rather than a part of the song — see NOTE_BARS. */
const isNotePart = (part) => part?.name === 'Note';

/**
 * Beats per bar. Only the numerator is read, matching useMetronomeEngine — which means 6/8 is
 * counted as six beats at the song's BPM rather than two dotted ones. Consistent with the
 * metronome is the right kind of wrong here: both features would be off together.
 */
export const getBeatsPerBar = (timeSignature) => {
  if (!timeSignature) return 4;
  const [numerator] = String(timeSignature).split('/');
  return parseInt(numerator, 10) || 4;
};

/**
 * Seconds per bar, or null when the song has no usable BPM. BPM is optional on a song and can
 * arrive as an empty string, so callers have to cope with null — see buildSongTimeline.
 */
export const getSecondsPerBar = (song) => {
  const bpm = Number(song?.bpm);
  if (!bpm || bpm <= 0) return null;
  return (getBeatsPerBar(song?.timeSignature) * 60) / bpm;
};

/**
 * Bars off a chords-only part, and whether the chart stated every one of them.
 *
 * Left unsnapped on purpose: a chords-only part is already counted a bar at a time off the chart,
 * and it measured well (median 1.15x against real playing) where the sung parts did not. Rounding
 * an honest count to a phrase would be adding a guess to a measurement.
 *
 * Bar lines, where someone wrote them, are better again — they are the chart saying outright how
 * long the part runs, and they catch the two cases a chord count cannot: one chord held over
 * several bars, and several chords sharing one. The fallback is per line, so a part half-written in
 * bar lines still reads each line the best way it can; `stated` is true only when no line needed
 * the fallback, because one guessed line makes the part's total a guess.
 */
const barsFromChordLines = (chordLines) => {
  let bars = 0;
  let stated = true;

  chordLines.forEach((line) => {
    const counted = countBars(line);
    if (counted == null) stated = false;
    // A line below the minimum is one the count didn't believe, so it is no longer the chart's
    const raw = counted ?? findChords(line).length;
    if (raw < MIN_BARS_PER_CHORD_LINE) stated = false;
    bars += Math.max(MIN_BARS_PER_CHORD_LINE, raw);
  });

  // The floor is the scheduler overriding the chart: a lone "| Dm |" is one bar played as two, so
  // the number written is not the number scrolled and the part has to stay open to correction
  return { bars: Math.max(MIN_INSTRUMENTAL_BARS, bars), stated: stated && bars >= MIN_INSTRUMENTAL_BARS };
};

/**
 * A part's lines as the estimator reads them. An instrumental's chart used to be written into its
 * lyrics as bar lines, and is timed exactly as it was then — from the bar lines it writes out as.
 */
export const getTimedLines = (part, beatsPerBar) => {
  const { strippedLyrics } = parseRepeatDirective(part?.lyrics || '');
  const lines = strippedLyrics.split(/\r\n|\r|\n/);

  if (!isChartDrawn(part) || !hasChartData(part?.chartData)) return { strippedLyrics, lines };

  return { strippedLyrics, lines: [...lines, ...chartDataToText(part.chartData, beatsPerBar).split('\n')] };
};

/**
 * Bars for one pass of a part, ignoring repeatCount, and whether they were measured off the chart
 * rather than estimated.
 *
 * A part whose body is words is timed off the chart stored beside them where it carries one, and
 * estimated from its lyrics lines where it doesn't. A part without lyrics is a chords-only
 * instrumental, measured by its bar lines when the chart carries them ("| Dm | Em C | B |") and
 * otherwise counted off its chords, where each chord written is roughly a bar of playing.
 *
 * `fromChart` is for estimatePartDuration alone: a stored chart is the one source that outranks a
 * stopwatched directive, so it has to know which parts had one before it reads the directive.
 */
const describePartBars = (part, beatsPerBar) => {
  if (isSectionPart(part)) return { bars: 0, measured: false }; // a divider in a mix, not music
  if (isNotePart(part)) return { bars: NOTE_BARS, measured: false }; // read, not played — see NOTE_BARS

  const { strippedLyrics, lines } = getTimedLines(part, beatsPerBar);

  const stated = chartBars(part, strippedLyrics, beatsPerBar);
  if (stated) {
    // Unsnapped, like the bar lines below: a chart is a count of the part, not a reading of it.
    // The floor is the same override — a chart shorter than a phrase is not the length it is played
    // at, so the part stays open to the pace multiplier's correction.
    return {
      bars: Math.max(MIN_INSTRUMENTAL_BARS, stated),
      measured: stated >= MIN_INSTRUMENTAL_BARS,
      fromChart: true,
    };
  }

  const lyricLines = lines.filter(isLyricsLine);
  const chordLines = lines.filter(isChordLine);

  if (lyricLines.length > 0) {
    // Two independent readings of the same part, and the longer wins. Lines undercount a verse
    // whose chords move faster than its words — two long lines over eight chords is eight bars,
    // not four — and the chords undercount a part whose chart only marks the changes. Taking the
    // larger was worth another 6 points of median accuracy over lines alone in the recording.
    //
    // Never measured from what is in the lyrics, however it is written: bar lines among the words
    // say how long those chords run, and a sung part is as long as the singing, which is the thing
    // being estimated here. The chart above is the exception — it is the part's chords in full,
    // which is a statement about the whole pass rather than about one line of it.
    const chordCount = chordLines.reduce((total, line) => total + findChords(line).length, 0);
    return { bars: snapToPhrase(Math.max(lyricLines.length * BARS_PER_LYRIC_LINE, chordCount)), measured: false };
  }

  if (chordLines.length > 0) {
    const { bars, stated } = barsFromChordLines(chordLines);
    return { bars, measured: stated };
  }

  return { bars: DEFAULT_INSTRUMENTAL_BARS, measured: false };
};

/** Bars for one pass of a part, ignoring repeatCount. `beatsPerBar` is the song's, for its chart. */
export const estimatePartBars = (part, beatsPerBar = 4) => describePartBars(part, beatsPerBar).bars;

/**
 * Passes of a part: the stored repeat count, or — where nobody set one — the count a {Nx,seconds}
 * directive names, or the number written into the lyrics by hand as slashes. Each is the chart
 * saying "play this twice" in the only place a part without a repeat count says it at all, so
 * reading them beats timing a repeated part as a single pass and racing through it.
 *
 * The directive's count matters here even though its seconds are read separately, because a chart
 * beside a directive takes the chart's bars and the directive's passes — the two are measuring
 * different things, and both are the player's own count. See countManualRepeats for what counts as
 * a mark.
 */
export const getRepeatCount = (part) => {
  const count = Number(part?.repeatCount);
  if (count > 1) return count;

  const { count: timedPasses } = parseRepeatDirective(part?.lyrics || '');

  return timedPasses > 1 ? timedPasses : countManualRepeats(part);
};

/**
 * One part's duration, in bars and (when the song has a BPM) in seconds.
 *
 * The exact source is returned as seconds only — it carries no bar count of its own, so the bar
 * figure is back-derived for the no-BPM fallback to work with.
 *
 * `measured` says the length came from the chart rather than from an estimate: bars a chart wrote
 * out in full, or a stopwatched directive. A repeat count multiplies straight through it — "play
 * this twice" is the chart speaking too, not a guess about it.
 */
export const estimatePartDuration = (part, secondsPerBar, beatsPerBar = 4) => {
  const repeatCount = getRepeatCount(part);
  const { bars: barsPerPass, measured, fromChart } = describePartBars(part, beatsPerBar);

  const { count, duration } = parseRepeatDirective(part?.lyrics || '');
  // A stored chart states the part's length outright, so it is read in preference to a stopwatch
  if (!fromChart && count && duration) {
    const seconds = count * duration;
    return {
      seconds,
      bars: secondsPerBar ? seconds / secondsPerBar : barsPerPass * repeatCount,
      exact: true,
      measured: true,
    };
  }

  const bars = barsPerPass * repeatCount;
  return { seconds: secondsPerBar ? bars * secondsPerBar : null, bars, exact: false, measured };
};

/**
 * Durations for a list of parts, keyed by part id.
 *
 * `unit` says what the numbers mean: 'seconds' when the song had a BPM, 'bars' when it didn't. In
 * the bars case the schedule normalises the total to a sensible wall-clock length instead, so the
 * relative shape of the song still drives the scroll — only its overall pace is assumed.
 */
export const buildSongTimeline = (song) => {
  const secondsPerBar = getSecondsPerBar(song);
  const beatsPerBar = getBeatsPerBar(song?.timeSignature);
  const durations = {};

  (song?.parts || []).forEach((part) => {
    // A part with its own tempo is timed at it; without a song BPM everything stays in bars
    const partBpm = secondsPerBar && getPartBpm(part, song.bpm);
    const partSecondsPerBar = partBpm ? getSecondsPerBar({ ...song, bpm: partBpm }) : secondsPerBar;
    const { seconds, bars, exact, measured } = estimatePartDuration(part, partSecondsPerBar, beatsPerBar);
    durations[part.id] = { seconds, bars, exact, measured };
  });

  return { durations, secondsPerBar, unit: secondsPerBar ? 'seconds' : 'bars' };
};
